import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getRequest, postRequest, patchRequest, deleteRequest } from "services/api";

import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";

const SalaryDetailContext = createContext();

export const SalaryDetailProvider = ({ children }) => {
  const [salaryDetails, setSalaryDetails] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    perPage: 10,
    include: "",
    orderBy: "created_at",
    sort: "desc",
    all: 0,
    salaryId: "",
    type: "", // insurance, facility, deduction, work_bonus, other
  });

  const queryString = useMemo(() => buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: filterOptions.perPage,
    include: filterOptions.include,
    orderBy: filterOptions.orderBy,
    sort: filterOptions.sort,
    all: filterOptions.all,
    'filter[salary_id]': filterOptions.salaryId,
    'filter[type]': filterOptions.type,
  }), [
    filterOptions.search,
    filterOptions.currentPage,
    filterOptions.perPage,
    filterOptions.include,
    filterOptions.orderBy,
    filterOptions.sort,
    filterOptions.all,
    filterOptions.salaryId,
    filterOptions.type,
  ]);

  const fetchSalaryDetails = useCallback(async () => {
    if (!filterOptions.salaryId) return;

    setSalaryDetails((prev) => ({ ...prev, loading: true }));

    try {
      const response = await getRequest(`/ahr/salary-details${queryString}`);
      if (response.success) {
        setSalaryDetails({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });
      } else {
        throw new Error("Failed to fetch salary details");
      }
    } catch (error) {
      setSalaryDetails((prev) => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
    }
  }, [queryString, filterOptions.salaryId]);

  useEffect(() => {
    fetchSalaryDetails();
  }, [fetchSalaryDetails]);

  const handleSearch = useCallback((e) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      if (searchValue !== filterOptions.search) {
        setFilterOptions((prev) => ({
          ...prev,
          search: searchValue,
          currentPage: 1,
        }));
      }
    }
  }, [filterOptions.search]);

  const handlePageChange = useCallback((page) => {
    setFilterOptions((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePerPageChange = useCallback((perPage) => {
    setFilterOptions((prev) => ({ ...prev, perPage: perPage, currentPage: 1 }));
  }, []);

  const handleSalaryIdChange = useCallback((salaryId) => {
    setFilterOptions((prev) => ({
      ...prev,
      salaryId: salaryId,
      currentPage: 1,
    }));
  }, []);

  const handleTypeChange = useCallback((type) => {
    setFilterOptions((prev) => ({
      ...prev,
      type: type,
      currentPage: 1,
    }));
  }, []);

  const Pagination = usePaginationWithComponent({
    totalPages: salaryDetails.pagination?.last_page || 1,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const createSalaryDetail = useCallback(async (data) => {
    try {
      const response = await postRequest(`/ahr/salary-details`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  const updateSalaryDetail = useCallback(async (id, data) => {
    try {
      const response = await patchRequest(`/ahr/salary-details/${id}`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  return (
    <SalaryDetailContext.Provider
      value={{
        salaryDetails,
        filterOptions,
        handleSearch,
        handlePerPageChange,
        handleSalaryIdChange,
        handleTypeChange,
        refetch: () => fetchSalaryDetails(),
        createSalaryDetail,
        updateSalaryDetail,
        deleteSalaryDetail: async (id) => {
          try {
            const response = await deleteRequest(`/ahr/salary-details/${id}`);
            return response;
          } catch (error) {
            throw error;
          }
        },
        Pagination,

      }}
    >
      {children}
    </SalaryDetailContext.Provider>
  );
};

export const useSalaryDetail = () => {
  const context = useContext(SalaryDetailContext);
  if (!context) {
    throw new Error("useSalaryDetail must be used within SalaryDetailProvider");
  }
  return context;
};
